/**
 * ExportFormModal Component
 *
 * Modal for exporting webforms to JSON format
 * Supports both single and bulk export with options
 */

import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { MdDownload, MdClose } from 'react-icons/md';
import toast from 'react-hot-toast';
import formExportImportService from '../../services/formExportImportService';

const ExportFormModal = ({ show, onHide, selectedForms = [] }) => {
    const [includeMetadata, setIncludeMetadata] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        if (!selectedForms || selectedForms.length === 0) {
            toast.error('No forms selected for export');
            return;
        }

        setIsExporting(true);

        try {
            let result;
            let filename;

            if (selectedForms.length === 1) {
                // Single form export
                const formId = selectedForms[0].form_id || selectedForms[0].id;
                result = await formExportImportService.exportForm(formId, includeMetadata);
                filename = `form-${formId}-${new Date().toISOString().split('T')[0]}.json`;
            } else {
                // Bulk export
                const formIds = selectedForms.map(f => f.form_id || f.id);
                result = await formExportImportService.exportFormsBulk(formIds, includeMetadata);
                filename = `forms-bulk-export-${new Date().toISOString().split('T')[0]}.json`;
            }

            if (result.success) {
                // Download the exported data
                const exportData = result.data.forms || result.data;
                formExportImportService.downloadAsJSON(exportData, filename);

                // Show warnings if any
                if (result.data.warnings && result.data.warnings.length > 0) {
                    const warningMessages = result.data.warnings
                        .map(w => w.message || w)
                        .join(', ');
                    toast.warning(`Export completed with warnings: ${warningMessages}`);
                } else {
                    toast.success(result.message || 'Forms exported successfully');
                }

                onHide();
            } else {
                toast.error(result.error || 'Failed to export forms');
            }
        } catch (error) {
            console.error('Export error:', error);
            toast.error('An error occurred during export');
        } finally {
            setIsExporting(false);
        }
    };

    const formCount = selectedForms?.length || 0;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <MdDownload className="me-2" />
                    Export Form{formCount > 1 ? 's' : ''}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {formCount === 0 ? (
                    <Alert variant="warning">
                        No forms selected for export. Please select at least one form.
                    </Alert>
                ) : (
                    <>
                        <Alert variant="info">
                            {formCount === 1 ? (
                                <>
                                    <strong>Selected form:</strong>{' '}
                                    {selectedForms[0].name || 'Unnamed Form'}
                                </>
                            ) : (
                                <>
                                    <strong>{formCount} forms selected</strong> for bulk export
                                </>
                            )}
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="include-metadata"
                                label="Include metadata (creation date, author, etc.)"
                                checked={includeMetadata}
                                onChange={(e) => setIncludeMetadata(e.target.checked)}
                            />
                            <Form.Text className="text-muted">
                                Metadata includes creation/update timestamps, creator info, and version details.
                            </Form.Text>
                        </Form.Group>

                        <Alert variant="secondary" className="mb-0">
                            <small>
                                <strong>Export includes:</strong>
                                <ul className="mb-0 mt-1">
                                    <li>Form structure (sections and fields)</li>
                                    <li>Validation rules and field configuration</li>
                                    <li>Permissions and department settings</li>
                                    {includeMetadata && <li>Creation/update metadata</li>}
                                </ul>
                                <strong className="mt-2 d-block">Export does NOT include:</strong>
                                <ul className="mb-0 mt-1">
                                    <li>Form submissions or draft data</li>
                                </ul>
                            </small>
                        </Alert>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isExporting}>
                    <MdClose className="me-1" />
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleExport}
                    disabled={isExporting || formCount === 0}
                >
                    {isExporting ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Exporting...
                        </>
                    ) : (
                        <>
                            <MdDownload className="me-1" />
                            Export to JSON
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExportFormModal;
